export default class IndexedDBManager {
    // Constructor to initialize database name and db instance
    constructor(dbName) {
        this.dbName = dbName
        this.db = null
        this.unavailable = false
    }

    // Method to initialize the database
    async init() {
        if (typeof window === 'undefined' || !window.indexedDB || typeof window.indexedDB.open !== 'function') {
            this.unavailable = true
            return
        }

        return new Promise((resolve) => {
            let request
            try {
                request = window.indexedDB.open(this.dbName)
            } catch (e) {
                this.unavailable = true
                resolve()
                return
            }

            // Event handler for when the database needs to be upgraded
            request.onupgradeneeded = (event) => {
                this.db = event.target.result
                // Create an object store named 'dataStore'
                this.db.createObjectStore('dataStore', { keyPath: 'id' })
            }

            // Event handler for successful database opening
            request.onsuccess = (event) => {
                this.db = event.target.result
                // Resolve the promise indicating success
                resolve()
            }

            request.onerror = () => {
                this.unavailable = true
                resolve()
            }
            request.onblocked = () => {
                this.unavailable = true
                resolve()
            }
        })
    }

    // Method to create a new entry
    async create(id, data, ttl) {
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        // Returning a new promise to handle the asynchronous operations
        return new Promise((resolve, reject) => {
            // Initiating a read-write transaction on the database
            const transaction = this.db.transaction('dataStore', 'readwrite')
            // Accessing the object store within the transaction
            const store = transaction.objectStore('dataStore')
            // Issuing a get request to check if an entry with the specified id already exists
            const getRequest = store.get(id)

            // Event handler for a successful get request
            getRequest.onsuccess = () => {
                // If an entry with the specified id already exists, reject the promise with an error
                if (getRequest.result !== undefined) {
                    reject(new Error('Entry with the provided ID already exists.'))
                    return
                }
                // Preparing the entry object to be added to the object store
                const entry = {
                    id,
                    data,
                    timestamp: Date.now(),
                    ttl: ttl * 1000  // Converting ttl to milliseconds
                }
                // Issuing an add request to add the new entry to the object store
                const addRequest = store.add(entry)
                // Event handler for a successful add request
                addRequest.onsuccess = () => resolve(addRequest.result)
                // Event handler for an error in the add request
                addRequest.onerror = (event) => reject(new Error(event.target.errorCode))
            }

            // Event handler for an error in the get request
            getRequest.onerror = (event) => reject(new Error(event.target.errorCode))
        })
    }

    // Method to update an existing entry
    async update(id, data, ttl) {
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        // Returning a new promise to handle the asynchronous operations
        return new Promise((resolve, reject) => {
            // Initiating a read-write transaction on the database
            const transaction = this.db.transaction('dataStore', 'readwrite')
            // Accessing the object store within the transaction
            const store = transaction.objectStore('dataStore')
            // Issuing a get request to check if an entry with the specified id exists
            const getRequest = store.get(id)

            // Event handler for a successful get request
            getRequest.onsuccess = () => {
                // If no entry with the specified id exists, reject the promise with an error
                if (getRequest.result === undefined) {
                    reject(new Error('No entry found with the provided ID.'))
                    return
                }
                // Preparing the updated entry object to be added to the object store
                const entry = {
                    id,
                    data,
                    timestamp: Date.now(),
                    ttl: ttl * 1000  // Converting ttl to milliseconds
                }
                // Issuing a put request to update the entry in the object store
                const putRequest = store.put(entry)
                // Event handler for a successful put request
                putRequest.onsuccess = () => resolve(putRequest.result)
                // Event handler for an error in the put request
                putRequest.onerror = (event) => reject(new Error(event.target.errorCode))
            }

            // Event handler for an error in the get request
            getRequest.onerror = (event) => reject(new Error(event.target.errorCode))
        })
    }


    // Method to create a new entry and update an existing entry
    async put(id, data, ttl) {
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        return new Promise((resolve, reject) => {
            // Initiating a read-write transaction on the database
            const transaction = this.db.transaction('dataStore', 'readwrite')
            // Accessing the object store within the transaction
            const store = transaction.objectStore('dataStore')

            // Preparing the entry object to be put into the object store
            const entry = {
                id,
                data,
                timestamp: Date.now(),
                ttl: ttl * 1000  // Converting ttl to milliseconds
            }

            // Issuing a put request to add/update the entry in the object store
            const putRequest = store.put(entry)

            // Event handler for a successful put request
            putRequest.onsuccess = () => resolve(putRequest.result)

            // Event handler for an error in the put request
            putRequest.onerror = (event) => reject(new Error(event.target.errorCode))
        })
    }

    // Method to retrieve an entry by id
    async retrieve(id) {
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction('dataStore', 'readonly')
            const store = transaction.objectStore('dataStore')
            const request = store.get(id)

            request.onsuccess = (event) => resolve(event.target.result)
            request.onerror = (event) => reject(new Error(event.target.errorCode))
        })
    }

    // Method to check for an entry by id
    async exists(id) {
        // Ensure the database has been initialized
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        return new Promise((resolve, reject) => {
            // Start a read-only transaction on the database
            const transaction = this.db.transaction('dataStore', 'readonly');

            // Access the object store within the transaction
            const store = transaction.objectStore('dataStore');

            // Issue a get request to retrieve the entry with the specified ID
            const getRequest = store.get(id);

            // Event handler for a successful get request
            getRequest.onsuccess = () => {
                // If result is not undefined, it means the entry exists
                resolve(getRequest.result !== undefined);
            }

            // Event handler for an error in the get request
            getRequest.onerror = (event) => {
                reject(new Error(event.target.errorCode));
            }
        });
    }

    // Method to clean expired entries
    async clean() {
        // Ensure that this.db has been initialized
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        // Return a new promise to handle the asynchronous nature of IndexedDB operations
        return new Promise((resolve, reject) => {
            // Initiating a read-write transaction on the database
            const transaction = this.db.transaction('dataStore', 'readwrite');
            if (!transaction) {
                reject(new Error('Unable to create transaction.'));
                return;
            }

            // Accessing the object store within the transaction
            const store = transaction.objectStore('dataStore');
            if (!store) {
                reject(new Error('Unable to access object store.'));
                return;
            }

            // Opening a cursor to iterate through all entries in the object store
            const request = store.openCursor();
            if (!request) {
                reject(new Error('Unable to open cursor.'));
                return;
            }

            // Event handler for a successful cursor open request
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    // Get the current entry from the cursor
                    const entry = cursor.value;
                    // Check if the entry has expired
                    if (Date.now() > entry.timestamp + entry.ttl) {
                        // If expired, delete the entry from the object store
                        const deleteRequest = store.delete(entry.id);
                        deleteRequest.onerror = (event) => reject(new Error(event.target.errorCode)); // Handle delete errors
                    }
                    // Continue to the next entry
                    cursor.continue();
                } else {
                    // No more entries, resolve the promise
                    resolve();
                }
            };

            // Event handler for an error in the cursor open request
            request.onerror = (event) => reject(new Error(event.target.errorCode));

            // Event handler to catch transaction errors
            transaction.onerror = (event) => reject(new Error(event.target.errorCode));
        });
    }

    // Method to delete all entries in the database
    async clearAll() {
        if (this.unavailable) return
        if (this.db === null) {
            throw new Error('Database has not been initialized. Call init method first.');
        }

        return new Promise((resolve, reject) => {
            // Start a read-write transaction on the database
            const transaction = this.db.transaction('dataStore', 'readwrite');

            // Access the object store within the transaction
            const store = transaction.objectStore('dataStore');

            // Clear all entries in the object store
            const clearRequest = store.clear();

            // Event handler for a successful clear request
            clearRequest.onsuccess = () => {
                resolve();  // Resolve the promise indicating success
            };

            // Event handler for an error in the clear request
            clearRequest.onerror = (event) => {
                reject(new Error('Error clearing the object store: ' + event.target.errorCode));
            };

            // Event handler to catch transaction errors
            transaction.onerror = (event) => {
                reject(new Error('Transaction error: ' + event.target.errorCode));
            };
        });
    }

}
